import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import Select from 'react-select';

// Common and Widgets
import Configs from '@/Configs';

/**
 * @typedef {import('./CountrySelect').CountrySelectProps} CountrySelectProps
 * @typedef {import('./CountrySelect').CountrySelectOption} CountrySelectOption
 */

const DEFAULT_SEARCH_DEBOUNCE_MS = 300;

/**
 * Searchable single-select for country codes. Options and loading are owned by the parent.
 *
 * When `onSearchChange` is set (Admin server search), client filtering is disabled and
 * typing is debounced to the parent. Without it (e.g. PLG static catalog), react-select
 * filters options locally as the user types.
 *
 * @param {CountrySelectProps} props
 * @returns {JSX.Element}
 */
function CountrySelect({
  options,
  isLoading = false,
  loadError = false,
  value,
  onChange,
  onSearchChange,
  searchDebounceMs = DEFAULT_SEARCH_DEBOUNCE_MS,
  isDisabled = false,
  inputId,
  className = '',
}) {
  const { t } = useTranslation();
  /** Preserves label when the current `options` omit the selected country (e.g. filtered list). */
  const [stickySelection, setStickySelection] = useState(
    /** @type {CountrySelectOption | null} */ (null)
  );
  const debounceRef = useRef(/** @type {ReturnType<typeof setTimeout> | null} */ (null));
  const usesServerSearch = typeof onSearchChange === 'function';

  const selectedOption = useMemo(() => {
    if (value == null || value === '') return null;
    const found = options.find((o) => o.value === value);
    if (found) return found;
    if (stickySelection?.value === value) return stickySelection;
    return /** @type {CountrySelectOption} */ ({ value, label: value });
  }, [value, options, stickySelection]);

  const handleChange = useCallback(
    (opt) => {
      setStickySelection(opt || null);
      onChange(opt ? opt.value : null);
    },
    [onChange]
  );

  const handleInputChange = useCallback(
    (inputValue, meta) => {
      if (!onSearchChange || meta.action !== 'input-change') return;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        onSearchChange(inputValue || '');
      }, searchDebounceMs);
    },
    [onSearchChange, searchDebounceMs]
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, []);

  return (
    <div
      className={`rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 ${className}`}
    >
      <Select
        inputId={inputId}
        instanceId={inputId || 'country-select'}
        isDisabled={isDisabled}
        isLoading={isLoading}
        isClearable
        isSearchable
        // Server search owns filtering; static catalogs use react-select's default match.
        filterOption={usesServerSearch ? () => true : undefined}
        options={options}
        value={selectedOption}
        onChange={handleChange}
        onInputChange={handleInputChange}
        styles={Configs.widgetDropdownStyle}
        placeholder={t('admin.company_create.country_placeholder', { ns: 'admin' })}
        className="text-xs font-work min-w-[200px] w-full"
        noOptionsMessage={() =>
          loadError
            ? t('admin.company_create.country_error', { ns: 'admin' })
            : t('admin.company_create.country_empty', { ns: 'admin' })
        }
      />
    </div>
  );
}

export default CountrySelect;
export { CountrySelect };
